import {
  Autocomplete,
  Box,
  Button,
  Grid,
  ListItemText,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ResourceColumnsChips from "../Organisms/ResourceColumnsChips";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import QueryCollapsible from "../Molecules/CommonCollapsible/QueryCollapsible";
import { IResearchQuery } from "../../types/researchQuery";
import useFetchResourceColumnsById from "../../hooks/useFetchResourceColumnsById";
import useFetchResourceColumnsByMultipleIds from "../../hooks/useFetchResourceColumnsByMultipleIds";
import { useRuleResourceContext } from "../../contexts/RuleResourceContext";
import {
  comparisonResearchQuerySchema,
  resourceQuerySchema,
} from "../../schemas";
import SQLEditor from "../QueryBuilder/SQL/SQLEditor";
import RenderVariables from "../Molecules/Resource/RenderVariables";
import {
  extractVariables,
  replaceQueryToDomainMapped,
} from "../../services/utils";
import * as Yup from "yup";
import CustomAccordion from "../Molecules/Accordian/CustomAccordion";
import QueryBuilderDialog from "./QueryBuilderDialog";
import { IconEyeBase } from "../../common/utils/icons";
import ViewResourceIconButton from "../ViewResourceIconButton";

interface ResourceQueryTabProps {
  checked: boolean;
  handleTransition: any;
  researchQueryData: IResearchQuery[];
  setResearchQueryData: Dispatch<SetStateAction<IResearchQuery[]>>;
  resourcesData: any;
  renderFrom: string;
  ruleData?: any;
  columns: any;
  setColumns: Dispatch<SetStateAction<any>>;
  isEditQuery: any;
  setIsEditQuery: any;
  editFormData: any;
  setQueryType: any;
  setEditFormData: any;
  handleSaveResearchQuery?: (researchQuery: IResearchQuery[]) => void;
  setEditorValue?: any;
  handleCancelClick?: () => void;
  setIsAddModeActive?: any;
  isFromListPage?: boolean;
  setErrors?: any;
  selectedRule?: any;
  isFromExecution?: boolean;
}

const ResourceQueryTab = ({
  handleSaveResearchQuery,
  researchQueryData,
  setResearchQueryData,
  checked,
  handleTransition,
  resourcesData,
  renderFrom,
  ruleData,
  columns,
  setColumns,
  isEditQuery,
  setIsEditQuery,
  editFormData,
  setQueryType,
  setEditFormData,
  setEditorValue,
  handleCancelClick,
  setIsAddModeActive,
  isFromListPage,
  setErrors,
  selectedRule,
  isFromExecution = false,
}: ResourceQueryTabProps) => {
  const {
    setIsLoading,
    editorRef,
    queryBuilderTempValue,
    setQueryBuilderTempValue,
    globalVariables,
    tempGlobalVariables,
    setTempGlobalVariables,
    setGlobalVariables,
  } = useRuleResourceContext();
  const [formData, setFormData] = useState<any>({
    query: "SELECT * FROM <>",
  });
  const [resourceColumnId, setResourceColumnId] = useState<any>(0);
  const [queryError, setQueryError] = useState<{ [key: string]: string }>({});
  const [mergedQueryColumns, setMergedQueryColumns] = useState<any>();
  const [resColumns, setResColumns] = useState<any>([]);
  const [variables, setVariables] = useState<any>([]);
  const [openQueryDialogIndex, setOpenQueryDialogIndex] = useState<
    number | null
  >(null);
  const [additionalResourceColIds, setAdditionalResourceColIds] = useState<
    number[]
  >([]);

  const [fetchedresourceColumnData] = useFetchResourceColumnsById({
    resourceColumnId,
    setIsLoading,
  });

  const [additionalResourceColumnsData] = useFetchResourceColumnsByMultipleIds({
    resourceColIds: additionalResourceColIds,
    setIsLoading,
  });
  useEffect(() => {
    if (renderFrom === "resource") {
      setFormData((prev: any) => ({
        ...prev,
        source: {
          ...prev?.source,
          resource_id: resourcesData?.id,
        },
      }));
    }
  }, []);

  useEffect(() => {
    setResColumns([]);
    if (fetchedresourceColumnData) {
      const columns =
        fetchedresourceColumnData?.resource_column_properties?.resource_columns?.map(
          (col: any) => ({
            name: col?.column_name,
            value: col?.column_name,
          }),
        ) || [];

      setResColumns(columns);
      setColumns((prev: any) => ({
        ...prev,
        resourceColumns: columns ?? [],
      }));
    }
  }, [fetchedresourceColumnData]);

  // Process additional resource columns data
  useEffect(() => {
    if (
      additionalResourceColumnsData &&
      Array.isArray(additionalResourceColumnsData) &&
      additionalResourceColumnsData.length > 0 &&
      resourcesData
    ) {
      // Get base resource columns for conflict detection
      const baseColumns = resColumns.map((col: any) => col.name);

      // Track all column names to detect conflicts
      const columnNameCount = new Map<string, number>();

      // Count base resource columns
      baseColumns.forEach((col: string) => {
        const count = columnNameCount.get(col) || 0;
        columnNameCount.set(col, count + 1);
      });

      // Count additional resource columns
      additionalResourceColumnsData.forEach((resourceColumnData: any) => {
        const columns =
          resourceColumnData?.resource_column_properties?.resource_columns ||
          [];
        columns.forEach((col: any) => {
          if (col?.column_name) {
            const count = columnNameCount.get(col.column_name) || 0;
            columnNameCount.set(col.column_name, count + 1);
          }
        });
      });

      // Format additional resource columns with conflict resolution
      const formattedAdditionalColumns = additionalResourceColumnsData
        .map((resourceColumnData: any) => {
          // Find the actual resource data to get resource_name and resource_code
          const resource = resourcesData?.find(
            (r: any) =>
              r.additional_properties?.resource_column_details_id ===
              resourceColumnData?.id,
          );

          const resourceCode =
            resource?.code || resource?.resource_prefix || "";
          const resourceName =
            resource?.resource_name || `Resource ${resourceColumnData?.id}`;

          const columns =
            resourceColumnData?.resource_column_properties?.resource_columns?.map(
              (col: any) => {
                const hasConflict =
                  (columnNameCount.get(col?.column_name) || 0) > 1;
                const displayName = hasConflict
                  ? `${col?.column_name}_${resourceCode}`
                  : col?.column_name;

                return {
                  name: displayName,
                  value: displayName,
                  originalName: col?.column_name,
                  hasConflict: hasConflict,
                };
              },
            ) || [];

          return {
            name: resourceName,
            resourceCode: resourceCode,
            columns: columns,
          };
        })
        .filter((item: any) => item.columns.length > 0);

      setColumns((prev: any) => ({
        ...prev,
        additionalResourceColumns: formattedAdditionalColumns,
      }));
    } else {
      // Clear additional resource columns if no data
      setColumns((prev: any) => ({
        ...prev,
        additionalResourceColumns: [],
      }));
    }
  }, [additionalResourceColumnsData, resColumns, resourcesData]);

  // useEffect(() => {
  //   if (columns) {
  //     setMergedQueryColumns({
  //       missingColumns: columns?.missingColumns,
  //       mismatchedColumns: columns?.missingColumns,
  //       resourceColumns: formData?.source?.resource_id ? resColumns : [],
  //       mergedColumns: [],
  //     });
  //   }
  // }, [resColumns, columns]);

  useEffect(() => {
    if (resColumns) {
      setColumns((prev: any) => ({
        ...prev,
        resourceColumns: formData?.source?.resource_id ? resColumns : [],
        mergedColumns: [],
      }));
    }
  }, [resColumns]);

  useEffect(() => {
    let resColumnId: string | number = 0;
    let resourceCode: string = "";
    let selectedResource: any = null;

    // if (!formData?.source?.resource_id) {
    //   setQueryBuilderTempValue(`SELECT * FROM <> WHERE [] in `);
    //   return;
    // }
    if (renderFrom === "rule") {
      selectedResource = resourcesData?.find(
        (option: { id: any }) => option.id === formData?.source?.resource_id,
      );
      resColumnId =
        selectedResource?.additional_properties?.resource_column_details_id;
      resourceCode = selectedResource?.code;

      // Extract additional resource column IDs
      const additionalResourceData =
        selectedResource?.additional_properties?.additional_resource_data;
      if (
        additionalResourceData &&
        Array.isArray(additionalResourceData) &&
        additionalResourceData.length > 0
      ) {
        // Extract resource IDs from additional_resource_data
        const additionalResIds = additionalResourceData
          .map((item: any) => item?.resource_id)
          .filter(Boolean);

        // Find corresponding resource_column_details_id for each additional resource
        const additionalColIds = additionalResIds
          .map((resId: number) => {
            const resource = resourcesData?.find((r: any) => r.id === resId);
            return resource?.additional_properties?.resource_column_details_id;
          })
          .filter(Boolean);

        setAdditionalResourceColIds(additionalColIds);
      } else {
        setAdditionalResourceColIds([]);
      }
    } else if (renderFrom === "resource") {
      resColumnId =
        resourcesData?.additional_properties?.resource_column_details_id;
      setAdditionalResourceColIds([]);
    }
    if (!isEditQuery) {
      setQueryBuilderTempValue(
        `SELECT * FROM <${resourceCode ? resourceCode : ""}>`,
      );
    }
    setResourceColumnId(resColumnId);
  }, [formData?.source?.resource_id, ruleData, renderFrom, resourcesData]);
  useEffect(() => {
    const queryVariables: any = extractVariables(queryBuilderTempValue);
    setVariables(queryVariables ?? []);
  }, [queryBuilderTempValue]);

  useEffect(() => {
    if (variables?.length > 0 && globalVariables) {
      const globalVars: any = {};
      variables.forEach((variable: any) => {
        if (globalVariables[variable] !== undefined) {
          globalVars[variable] = globalVariables[variable];
        } else if (tempGlobalVariables[variable] !== undefined) {
          globalVars[variable] = tempGlobalVariables[variable];
        } else {
          globalVars[variable] = "";
        }
      });
      // Compare the current tempLocalVariables with the new ones to avoid unnecessary updates
      if (JSON.stringify(tempGlobalVariables) !== JSON.stringify(globalVars)) {
        // setTempLocalVariables(globalVars);
        setTempGlobalVariables(globalVars);
      }
    } else {
      setTempGlobalVariables({});
    }
  }, [variables]);

  const validateField = async (name: any, value: any) => {
    try {
      // Create a partial form data object with only the field being changed
      const partialFormData = { ...formData, [name]: value };
      await resourceQuerySchema.validateAt(name, partialFormData);
      // If validation passes, clear any previous errors for this field
      setQueryError((prevErrors: any) => ({
        ...prevErrors,
        [name]: undefined,
      }));
    } catch (validationError: any) {
      // If validation fails, set the error message for this field
      setQueryError((prevErrors: any) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  const validateSourceField = async (name: any, value: any) => {
    try {
      const partialFormData = {
        source: {
          [name]: value,
        },
      };
      const validateFieldName = `source.${name}`;
      await resourceQuerySchema.validateAt(validateFieldName, partialFormData);
      // If validation passes, clear any previous errors for this field
      setQueryError((prevErrors: any) => ({
        ...prevErrors,
        [name]: undefined,
      }));
    } catch (validationError: any) {
      // If validation fails, set the error message for this field
      setQueryError((prevErrors: any) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };
  const handleChangeQuery = (event: any) => {
    const { name, value } = event.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
    validateField(name, value);
  };
  const handleSaveQuery = async () => {
    try {
      if (!isFromExecution) {
        comparisonResearchQuerySchema.validateSync(
          { rule_id: selectedRule?.id },
          { abortEarly: false },
        );
      }
      try {
        const researchQuery: any = [...researchQueryData];

        const updatedFormData = {
          ...formData,
          query: queryBuilderTempValue,
        };
        // Check for duplicate query names
        const isDuplicate = researchQuery.some(
          (query: any) =>
            !(isEditQuery && query.id === formData.id) &&
            query.name?.toLowerCase().trim() ===
              formData.name?.toLowerCase().trim(),
        );

        setQueryError((prev: any) => ({
          ...prev,
          name: isDuplicate ? "Query name is duplicate" : "",
        }));
        if (isDuplicate) return;

        if (!isFromExecution) {
          await resourceQuerySchema.validate(updatedFormData, {
            abortEarly: false,
          });
        }

        // Variable validations
        const variableSchema: any = {};
        Object.keys(tempGlobalVariables).map((key: any) => {
          if (tempGlobalVariables[key] === "") {
            variableSchema[key] = Yup.string().required(`Please enter ${key}`);
            setQueryError((prevErrors) => ({
              ...prevErrors,
              [key]: `Please enter ${key}`,
            }));
          } else {
            return {
              name: key,
              value: tempGlobalVariables[key],
            };
          }
        });
        const resourceQueryVariableSchema = Yup.object().shape(variableSchema);
        if (!isFromExecution) {
          await resourceQueryVariableSchema.validate(tempGlobalVariables, {
            abortEarly: false,
          });
        }
        setGlobalVariables((prev: any) => ({
          ...Object.keys(tempGlobalVariables).reduce(
            (acc, key) => ({ ...acc, [key]: tempGlobalVariables[key] }),
            prev,
          ),
        }));

        const updatedQuery: string = replaceQueryToDomainMapped(
          queryBuilderTempValue,
          columns?.missingColumns,
        );

        if (isEditQuery) {
          const index = researchQuery.findIndex(
            (item: any) => item.id === formData.id,
          );
          if (index !== -1) {
            researchQuery[index] = {
              id: formData.id,
              name: formData.name,
              query: queryBuilderTempValue,
              updatedQuery: updatedQuery,
              source: {
                type: "Resource",
                resource_id: formData?.source?.resource_id,
                resource_code: formData?.source?.resource_code,
              },
            };
          }
        } else {
          researchQuery.push({
            id: researchQuery.length > 0 ? researchQuery.length + 1 : 1,
            name: formData?.name,
            query: queryBuilderTempValue,
            updatedQuery: updatedQuery,
            source: {
              type: "Resource",
              resource_id: formData?.source?.resource_id,
              resource_code: formData?.source?.resource_code,
            },
          });
        }

        setResearchQueryData([...researchQuery]);

        setFormData({ name: "" });
        setTempGlobalVariables({});
        setQueryBuilderTempValue(`SELECT * FROM <> WHERE [] in `);
        handleTransition(false);
        setIsEditQuery(false);
        if (isFromListPage) {
          setIsAddModeActive(true);
        }
        setQueryType("");

        handleSaveResearchQuery && handleSaveResearchQuery(researchQuery);
      } catch (validationErrors: any) {
        const newErrors: { [key: string]: string } = {};
        if (validationErrors.inner) {
          validationErrors.inner.forEach(
            (error: { path: string | number; message: string }) => {
              const fieldName = String(error.path).replace(/^source?\./, "");
              newErrors[fieldName] = error.message;
            },
          );
        }
        setQueryError((prevErrors: any) => ({
          ...prevErrors,
          ...newErrors,
        }));
      }
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      if (validationErrors.inner) {
        validationErrors.inner.forEach(
          (error: { path: string | number; message: string }) => {
            const fieldName = String(error.path).replace(/^source?\./, "");
            newErrors[fieldName] = error.message;
          },
        );
      }
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };

  const handleColumnSelect = (column: string, columnName: string) => {
    if (!editorRef.current) return;
    const editor = editorRef.current.editor;
    if (!editor) return;

    const currentPosition = editor.getCursorPosition();
    const currentColumn = currentPosition.column;

    let updatedColumn = `${column} `;

    if (columnName === "missing") {
      updatedColumn = `"${column}" `;
    } else if (columnName === "mismatched") {
      updatedColumn = `"${column}" `;
    } else if (columnName === "merged") {
      updatedColumn = `"${column}" `;
    }

    editor.session.insert(currentPosition, updatedColumn);
    setEditorValue(editor.session.getValue());

    const newCursorPosition = {
      row: currentPosition.row,
      column: currentColumn + updatedColumn.length,
    };

    editor.moveCursorToPosition(newCursorPosition);
    editor.clearSelection();
    editor.focus();
  };

  useEffect(() => {
    if (isEditQuery) {
      setFormData(editFormData);
    }
  }, [editFormData]);

  return (
    <>
      <Grid item xl={3} lg={3} md={3} sm>
        <TextField
          label={
            <span>
              Query Name
              <span className="required-asterisk">*</span>
            </span>
          }
          name="name"
          type="text"
          fullWidth
          variant="outlined"
          // className="form-control-autocomplete"
          value={formData?.name}
          onChange={handleChangeQuery}
          error={!!queryError?.name}
          helperText={queryError?.name || ""}
          className={`form-control-autocomplete
                            ${queryError?.name ? "has-error" : ""}
                          `}
        />
      </Grid>
      {renderFrom === "rule" && (
        <>
          <Grid item xl={3} lg={3} md={3} sm>
            <Box className="d-flex align-items-end cols-gap-6">
              <Autocomplete
                fullWidth
                options={resourcesData ?? []}
                getOptionLabel={(option: any) => option.resource_name || ""}
                value={
                  resourcesData?.find(
                    (option: { id: any }) =>
                      option.id === formData?.source?.resource_id,
                  ) || null
                }
                renderInput={(params) => (
                  <TextField
                    label={
                      <span>
                        Select Resources
                        <span className="required-asterisk">*</span>
                      </span>
                    }
                    name="resource"
                    style={{ color: "#000000" }}
                    {...params}
                    placeholder="Select..."
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!queryError?.resource_id}
                    helperText={queryError?.resource_id || ""}
                  />
                )}
                renderOption={(params: any, item: any) => (
                  <li
                    {...params}
                    key={item.key}
                    style={{
                      paddingTop: "2px",
                      paddingBottom: "2px",
                    }}
                  >
                    <ListItemText>{item.resource_name}</ListItemText>
                  </li>
                )}
                loadingText="Loading..."
                onChange={(event: any, value: any) => {
                  setFormData((prev: any) => ({
                    ...prev,
                    source: {
                      ...prev.source,
                      ["resource_id"]: value?.id,
                      ["resource_code"]: value?.code,
                    },
                  }));
                  setQueryBuilderTempValue(
                    `SELECT * FROM <${value?.code ? value?.code : ""}>`,
                  );
                  setResColumns([]);
                  validateSourceField("resource_id", value?.id);
                }}
                // className={`form-control-autocomplete`}
                className={`form-control-autocomplete
                            ${queryError?.resource_id ? "has-error" : ""}
                          `}
              />
              {formData?.source?.resource_id && (
                <ViewResourceIconButton
                  resourceId={formData?.source?.resource_id}
                  tooltipText={"Click to view resource"}
                />
              )}
            </Box>
          </Grid>
          {/* {columns?.resourceColumns && columns?.resourceColumns.length > 0 && (
            <Grid item xl={12} lg={12} md={12} sm>
              <Box className="rs-query-column-accordion">
                <CustomAccordion
                  expandId="panel2d-header-support-documents"
                  title={"Resource Columns"}
                  isEnabled={columns?.missingColumns.length > 0}
                  isDefaultExpaned={true}
                  topMargin={1}
                  accodionTopGap={1}
                >
                  <div
                    style={{
                      maxHeight: "300px",
                      width: "100%",
                      overflowY: "auto",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {columns?.resourceColumns?.map((option: any) => (
                        <Chip
                          key={option.name}
                          label={option.name}
                          onClick={() =>
                            handleColumnSelect(option.name, "resource")
                          }
                        />
                      ))}
                    </Box>
                  </div>
                </CustomAccordion>
              </Box>
            </Grid>
          )} */}
        </>
      )}
      {renderFrom === "resource" && (
        <Grid item xl={3} lg={3} md={3} sm>
          <TextField
            label={
              <span>
                Resource Name
                <span className="required-asterisk">*</span>
              </span>
            }
            type="text"
            fullWidth
            variant="outlined"
            className="form-control-autocomplete"
            value={resourcesData?.resource_name}
            disabled={true}
          />
        </Grid>
      )}

      <QueryBuilderDialog
        columns={columns}
        setColumns={setColumns}
        index={15}
        openQueryDialogIndex={openQueryDialogIndex}
        setOpenQueryDialogIndex={setOpenQueryDialogIndex}
        isTextBoxRequied={true}
        queryType={"resource"}
        queryBuilderType="rule_research_query"
      />
      {/* <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        <div className="form-control">
          <SQLEditor
            error={queryError}
            setErrors={setQueryError}
            isQueryColumns={true}
            availableVariableMarginGap={1}
          />
        </div>
      </Grid>
      {Object.entries(tempGlobalVariables).length > 0 && (
        <RenderVariables error={queryError} setErrors={setQueryError} />
      )}
      <ResourceColumnsChips
        setEditorValue={setQueryBuilderTempValue}
        editorRef={editorRef}
        columns={columns}
      /> */}
      <Grid
        item
        xs={12}
        sm={12}
        md={12}
        lg={12}
        xl={12}
        sx={{
          display: "flex",
          columnGap: "8px",
          textAlign: "right",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          className="btn-orange btn-dark"
          onClick={() => {
            setFormData({
              name: "",
            });
            setQueryBuilderTempValue(`SELECT * FROM <> WHERE [] in `);
            setQueryError({});
            if (handleCancelClick) {
              handleCancelClick();
            } else {
              setQueryType("");
              setEditFormData({
                name: "",
              });
              handleTransition(false);
              setIsEditQuery(false);
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          className="btn-orange"
          onClick={handleSaveQuery}
        >
          <SaveOutlinedIcon /> Save
        </Button>
      </Grid>
    </>
  );
};

export default ResourceQueryTab;
